import type { View } from '../types';

const ADMIN_API_ORIGIN = 'https://admin.theviewshed.com';
/** Matches the `server.proxy` key in vite.config.ts. */
const DEV_PROXY_PREFIX = '/admin-api';

/**
 * Route an admin API URL through the Vite dev proxy in development.
 *
 * The admin API only sends CORS headers for the production origin, so calling
 * it directly from localhost fails every request and the app renders with no
 * data at all. In development the request goes to a same-origin path that Vite
 * forwards server-side; in production it is returned untouched.
 */
function adminApi(url: string): string {
  if (!import.meta.env.DEV || !url?.startsWith(ADMIN_API_ORIGIN)) {
    return url;
  }
  return DEV_PROXY_PREFIX + url.slice(ADMIN_API_ORIGIN.length);
}

const API_URL = adminApi(import.meta.env.VITE_PUBLIC_API_URL);
// DirectFlow API endpoint for view submissions
const DIRECTFLOW_SUBMIT_API_URL = adminApi(`${ADMIN_API_ORIGIN}/api/submissions/submit-view`);
// DirectFlow API endpoint for settings
const DIRECTFLOW_SETTINGS_API_URL = adminApi(`${ADMIN_API_ORIGIN}/api/settings`);

interface ApiView {
  id: number;
  record_id: number;
  source: string;
  state: string;
  country: string;
  continent: string;
  destination: string;
  dest_state: string;
  dest_country: string;
  dest_continent: string;
  distance_km: string;
  distance_m: string;
  type: string;
  status: string;
  resource_1: string;
  resource_2: string;
  resource_3: string;
  resource_4: string;
  notes: string;
  createdAt: string;
  listing_status: string;
  source_lat?: string;
  source_long?: string;
  dest_lat?: string;
  dest_long?: string;
}

interface SubmitFormData {
  source: string;
  state: string;
  country: string;
  continent?: string;
  destination: string;
  dest_state: string;
  dest_country: string;
  dest_continent?: string;
  distance_km: string;
  distance_m: string;
  status: string;
  type: string;
  source_lat?: string;
  source_long?: string;
  dest_lat?: string;
  dest_long?: string;
  description?: string;
  email?: string;
  submitterEmail?: string;
  submitterName?: string;
  creditInfo?: string;
  creditName?: string;
  resource_2?: string | null;
  formType?: string;
}

// Settings interface based on the API response
interface SiteSettings {
  adminEmail: string;
  analyticsTrackingId: string;
  autoApprovalCriteria: string;
  companyName: string;
  contactSubtitle: string;
  contactTitle: string;
  coordinateDisplayFormat: string;
  defaultCountry: string;
  defaultSubmissionStatus: string;
  distanceCalculationMethod: string;
  facebookUrl: string;
  footerCopyright: string;
  homepageHeroSubtitle: string;
  homepageHeroTitle: string;
  instagramUrl: string;
  maxFileUploadSize: number;
  /** r/FromAfar members, maintained by the client in admin settings. Sent as a string. */
  membersCount: string;
  metaDescription: string;
  openGraphImage: string;
  requiredFields: string;
  siteName: string;
  submitViewSubtitle: string;
  submitViewTitle: string;
  supportedImageFormats: string;
  supportEmail: string;
  twitterUrl: string;
}

// Cache for settings to avoid excessive API calls
let settingsCache: SiteSettings | null = null;
let settingsCacheTime: number = 0;
const SETTINGS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache for views to avoid repeated large API fetches (1.5 MiB payload)
let viewsCache: View[] | null = null;
let viewsCacheTime: number = 0;
let viewsInFlightPromise: Promise<View[]> | null = null;
const VIEWS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fetch site settings from DirectFlow API
export const fetchSiteSettings = async (): Promise<SiteSettings | null> => {
  try {
    // Check cache first
    const now = Date.now();
    if (settingsCache && (now - settingsCacheTime) < SETTINGS_CACHE_DURATION) {
      // Using cached settings
      return settingsCache;
    }

    // Fetching site settings
    
    const response = await fetch(DIRECTFLOW_SETTINGS_API_URL);
    
    if (!response.ok) {
      throw new Error(`Settings API request failed with status ${response.status}`);
    }
    
    const settings: SiteSettings = await response.json();
    
    // Update cache
    settingsCache = settings;
    settingsCacheTime = now;
    
          // Site settings fetched successfully
    return settings;
  } catch (error) {
    console.error('Error fetching site settings:', error);
    
    // Return default settings if API fails
    return {
      adminEmail: "admin@theviewshed.com",
      analyticsTrackingId: "UA-123456789-1",
      autoApprovalCriteria: "No specific criteria",
      companyName: "TheViewShed Inc.",
      contactSubtitle: "Get in touch with questions, feedback, or collaboration opportunities",
      contactTitle: "Contact Us",
      coordinateDisplayFormat: "Decimal Degrees",
      defaultCountry: "USA",
      defaultSubmissionStatus: "pending",
      distanceCalculationMethod: "Miles",
      facebookUrl: "https://www.facebook.com/theviewshed",
      footerCopyright: "© 2024 The View Shed. All rights reserved.",
      homepageHeroSubtitle: "Your gateway to breathtaking views",
      homepageHeroTitle: "Welcome to the Updated View Shed",
      instagramUrl: "https://www.instagram.com/theviewshed",
      maxFileUploadSize: 2097152,
      // Empty on purpose: with the API down there is no client-set count, so
      // useSubredditStats falls through to its own constant rather than
      // rendering a stale number baked in here.
      membersCount: "",
      metaDescription: "The View Shed is a platform for sharing and discovering distant views",
      openGraphImage: "https://example.com/open-graph-image.jpg",
      requiredFields: "Name, Location, Description",
      siteName: "Updated View Shed",
      submitViewSubtitle: "Share your discovery of a distant view with the community",
      submitViewTitle: "Submit a New View",
      supportedImageFormats: "JPEG, PNG, GIF, WebP",
      supportEmail: "support@theviewshed.com",
      twitterUrl: "https://twitter.com/theviewshed"
    };
  }
};

// Clear settings cache (useful for admin updates)
export const clearSettingsCache = (): void => {
  settingsCache = null;
  settingsCacheTime = 0;
};

// Clear views cache (useful after new submissions or manual refresh)
export const clearViewsCache = (): void => {
  viewsCache = null;
  viewsCacheTime = 0;
};

// Transform API data to match our app's View type
const transformApiData = (apiView: ApiView): View => {
  // Use resource_2 as image if available and valid, otherwise use a placeholder
  const placeholderImage = "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=80&w=1000&ixlib=rb-4.0.3";
  
  // Check if resource_2 exists and is a valid URL
  let image = placeholderImage;
  if (apiView.resource_2 && isValidImageUrl(apiView.resource_2)) {
    image = apiView.resource_2;
  }
  
  // Normalize the status value to handle different format variations from API
  const normalizeApiStatus = (status: string): "confirmed" | "likely" | "ifclear" | "plausible" | "dubious" | "unlikely" => {
    const lowerStatus = status.toLowerCase();
    
    // Handle all variations of "if clear"
    if (lowerStatus === 'if-clear' || lowerStatus === 'if clear' || lowerStatus === 'ifclear') {
      return 'ifclear';
    }
    
    // Handle other statuses
    if (lowerStatus === 'confirmed') return 'confirmed';
    if (lowerStatus === 'likely') return 'likely';
    if (lowerStatus === 'plausible') return 'plausible';
    if (lowerStatus === 'dubious') return 'dubious';
    if (lowerStatus === 'unlikely') return 'unlikely';
    
    // Default fallback
    return 'plausible';
  };
  
  // Map status values to ensure consistent format, but preserve all 6 categories
  const statusMap: Record<string, "confirmed" | "likely" | "ifclear" | "plausible" | "dubious" | "unlikely"> = {
    'confirmed': 'confirmed',
    'likely': 'likely',
    'if-clear': 'ifclear',  // normalize the format (remove dash)
    'if clear': 'ifclear',  // without dash
    'ifclear': 'ifclear',   // handle both formats
    'plausible': 'plausible',
    'dubious': 'dubious',
    'unlikely': 'unlikely'
  };

  
  // Generate a color based on the status
  const colorMap: Record<string, string> = {
    'confirmed': 'bg-green-500',
    'likely': 'bg-blue-500',
    'ifclear': 'bg-cyan-500',
    'plausible': 'bg-yellow-500',
    'dubious': 'bg-orange-500',
    'unlikely': 'bg-red-500'
  };

  // Get normalized status
  const normalizedStatus = normalizeApiStatus(apiView.status);

  // Generate a unique ID if record_id is null
  const id = apiView.record_id ? apiView.record_id.toString() : 
    `${apiView.source}-${apiView.destination}-${Date.now()}`;

  // Debug: Log resource_1 content to see if it contains notes
  // Check for notes in API data

  // Infer view type from destination name or other fields when type is missing
  let viewType = apiView.type;
  if (!viewType) {
    const destination = (apiView.destination || '').toLowerCase();
    const source = (apiView.source || '').toLowerCase();
    
    // Check for patterns in destination or source names to infer view type
    if (destination.includes('mountain') || 
        destination.includes('mount ') || 
        destination.includes(' mt ') || 
        destination.includes(' mt.') || 
        destination.includes(' peak') || 
        destination.includes(' hill')) {
      viewType = 'Mountain(s)';
    } 
    else if (destination.includes('skyline') || 
             destination.includes('city') || 
             destination.includes('downtown')) {
      viewType = 'Skyline';
    }
    else if (destination.includes('coast') || 
             destination.includes('shore') || 
             destination.includes('beach')) {
      viewType = 'Coast';
    }
    else if (destination.includes('island') || 
             destination.includes(' isle')) {
      viewType = 'Island';
    }
    else if (destination.includes('bridge') || 
             destination.includes('viaduct')) {
      viewType = 'Bridge';
    }
    else if (destination.includes('lake') || 
             destination.includes('river') || 
             destination.includes('bay') || 
             destination.includes('ocean') || 
             destination.includes('sea')) {
      viewType = 'Water Body';
    }
    else if (destination.includes('chimney') || 
             destination.includes('stack') || 
             destination.includes('tower')) {
      viewType = 'Chimney';
    }
    else {
      // Default to Mountain if no specific type could be inferred
      viewType = 'Mountain(s)';
    }
    
    // Inferred type for destination
  }

  // distance_km / distance_m arrive as strings ("825.59"). Parse them once here
  // so no consumer has to pull numbers back out of the formatted string.
  // Despite its name, distance_m is miles.
  const distanceKm = Number.parseFloat(apiView.distance_km);
  const distanceMi = Number.parseFloat(apiView.distance_m);

  return {
    id,
    distance: `${apiView.distance_km} km / ${apiView.distance_m} mi`,
    distanceKm: Number.isFinite(distanceKm) ? distanceKm : 0,
    distanceMi: Number.isFinite(distanceMi) ? distanceMi : 0,
    source: apiView.source || 'Unknown Source',
    state: apiView.state || '',
    country: apiView.country || '',
    continent: apiView.continent || '',
    destination: apiView.destination || 'Unknown Destination',
    destinationState: apiView.dest_state || '',
    destinationCountry: apiView.dest_country || '',
    destinationContinent: apiView.dest_continent || '',
    status: normalizedStatus,
    image,
    color: colorMap[normalizedStatus],
    type: viewType, // Add the inferred or original type
    resource_3: apiView.resource_3 || '',
    resource_4: apiView.resource_4 || '',
    notes: apiView.notes || '', // Use the notes field from API data
    source_lat: apiView.source_lat || '',
    source_long: apiView.source_long || '',
    dest_lat: apiView.dest_lat || '',
    dest_long: apiView.dest_long || ''
  };
};

// Helper function to check if a URL is likely a valid image
function isValidImageUrl(url: string): boolean {
  if (!url) return false;
  
  // Check if the URL has a valid image extension
  const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
  const hasValidExtension = validExtensions.some(ext => url.toLowerCase().endsWith(ext));
  
  // Check if it's a URL from common image hosting services
  const isFromImageHost = 
    url.includes('unsplash.com') || 
    url.includes('imgur.com') || 
    url.includes('cloudinary.com') || 
    url.includes('images.') ||
    url.includes('.jpg?') ||
    url.includes('.png?') ||
    url.includes('.jpeg?') ||
    url.includes('.webp?');
  
  // Check if it's a Base64 encoded image
  const isBase64Image = url.startsWith('data:image/');
  
  return hasValidExtension || isFromImageHost || isBase64Image;
}

// Helper function to ensure Base64 image is properly formatted
function validateAndFormatBase64Image(base64String: string): string {
  if (!base64String) return '';
  
  // Ensure it's properly formatted as a data URL
  if (base64String.startsWith('data:image/')) {
    // Already properly formatted
    return base64String;
  }
  
  // Try to detect the image type from the Base64 header
  const imageType = detectImageTypeFromBase64(base64String);
  if (imageType) {
    return `data:image/${imageType};base64,${base64String.replace(/^data:image\/[a-z]+;base64,/, '')}`;
  }
  
  // Default to JPEG if we can't determine the type
  return `data:image/jpeg;base64,${base64String.replace(/^data:image\/[a-z]+;base64,/, '')}`;
}

// Helper function to detect image type from Base64 string
function detectImageTypeFromBase64(base64String: string): string | null {
  // Remove header if it exists
  const base64Data = base64String.replace(/^data:image\/[a-z]+;base64,/, '');
  
  // Check first few characters to determine image type
  try {
    const binaryString = atob(base64Data.slice(0, 24));
    
    // Check for JPEG
    if (binaryString.charCodeAt(0) === 0xFF && binaryString.charCodeAt(1) === 0xD8) {
      return 'jpeg';
    }
    
    // Check for PNG
    if (binaryString.charCodeAt(0) === 0x89 && binaryString.charCodeAt(1) === 0x50) {
      return 'png';
    }
    
    // Check for GIF
    if (binaryString.charCodeAt(0) === 0x47 && binaryString.charCodeAt(1) === 0x49) {
      return 'gif';
    }
    
    // Check for WEBP
    if (binaryString.includes('WEBP')) {
      return 'webp';
    }
  } catch (e) {
    console.error('Error detecting image type:', e);
  }
  
  return null;
}

// Helper function to upload an image and get a URL
// COMMENTED OUT: This function was converting Base64 to placeholder URLs instead of sending Base64 directly
// async function uploadImageAndGetUrl(imageData: string): Promise<string | null> {
//   if (!imageData) return null;
//   
//   // If it's already a URL, just return it
//   if (imageData.startsWith('http')) {
//     console.log('Using existing image URL');
//     return imageData;
//   }
//   
//   // If it's not a Base64 image, return null
//   if (!imageData.startsWith('data:image/')) {
//     console.warn('Invalid image data format');
//     return null;
//   }
//   
//   try {
//     // For testing purposes, return a known working image URL
//     // In production, this would upload the Base64 data to a service and return the URL
//     console.log('Converting Base64 to URL for API compatibility...');
//     return 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=80&w=1000&ixlib=rb-4.0.3';
//   } catch (error) {
//     console.error('Error handling image:', error);
//     return null;
//   }
// }

export const fetchViews = async (): Promise<View[]> => {
  // Return cached data if still fresh
  const now = Date.now();
  if (viewsCache && (now - viewsCacheTime) < VIEWS_CACHE_DURATION) {
    return viewsCache;
  }

  // Deduplicate concurrent requests — return the same in-flight promise
  if (viewsInFlightPromise) {
    return viewsInFlightPromise;
  }

  viewsInFlightPromise = (async () => {
  try {
    // First request to get pagination info and first page of data
    const initialResponse = await fetch(`${API_URL}?limit=2000`);
    
    if (!initialResponse.ok) {
      throw new Error(`API request failed with status ${initialResponse.status}`);
    }
    
    const initialData = await initialResponse.json();
    
    // Log the raw data for debugging
    // Initial API Response received
    
    // Extract the listings array and pagination info from the response
    const listings = initialData.listings || [];
    const pagination = initialData.pagination || { page: 1, totalPages: 1 };
    
    let allListings = [...listings];
    
    // If there are more pages, fetch them
    if (pagination.hasNextPage) {
      // Fetching additional pages of data
      
      // Fetch remaining pages
      for (let page = 2; page <= pagination.totalPages; page++) {
        // Fetching next page
        const pageResponse = await fetch(`${API_URL}?page=${page}&limit=2000`);
        
        if (!pageResponse.ok) {
          console.error(`Failed to fetch page ${page}`);
          continue;
        }
        
        const pageData = await pageResponse.json();
        const pageListings = pageData.listings || [];
        
        // Add these listings to our collection
        allListings = [...allListings, ...pageListings];
        // Added listings from page
      }
    }
    
    // Total listings fetched
    
    if (!Array.isArray(allListings)) {
      console.error('API did not return a valid listings array:', allListings);
      return [];
    }
    
    // Filter out any invalid entries and transform the data
    const validViews = allListings
      .filter(view => view && (view.source || view.destination)) // Ensure at least source or destination exists
      .map(transformApiData);
    
    // Transformed Views processed

    // Update cache
    viewsCache = validViews;
    viewsCacheTime = Date.now();

    return validViews;
  } catch (error) {
    console.error('Error fetching views:', error);
    return [];
  } finally {
    viewsInFlightPromise = null;
  }
  })();

  return viewsInFlightPromise;
};

export const submitView = async (formData: SubmitFormData): Promise<boolean> => {
  try {
    // Use the constant instead of hardcoding the URL
    // Attempting to submit view to API
    
    // Process image if present - send Base64 directly to API
    let processedResource2 = formData.resource_2 || '';
    
    // If it's already a URL, keep it as is
    if (processedResource2.startsWith('http')) {
      // Using existing image URL
    } else if (processedResource2.startsWith('data:image/')) {
      // Send Base64 data directly to API - DO NOT convert to placeholder URL
              // Sending Base64 image data directly to API
      // Keep the Base64 data as is
    } else if (processedResource2) {
      console.warn('Invalid image format, will not include in submission');
      processedResource2 = '';
    }
    
    // Format the payload exactly as expected by the API
    const payload = {
      source: formData.source || '',
      state: formData.state || '',
      country: formData.country || '',
      continent: formData.continent || 'North America',
      destination: formData.destination || '',
      dest_state: formData.dest_state || '',
      dest_country: formData.dest_country || '',
      dest_continent: formData.dest_continent || 'North America',
      distance_km: formData.distance_km || '0',
      distance_m: formData.distance_m || '0',
      status: formData.status || 'confirmed',
      type: formData.type || 'Mountain(s)',
      source_lat: formData.source_lat || '',
      source_long: formData.source_long || '',
      dest_lat: formData.dest_lat || '',
      dest_long: formData.dest_long || '',
      description: formData.description || '',
      email: formData.email || '',
      submitterEmail: formData.submitterEmail || formData.email || '',
      submitterName: formData.submitterName || 'Anonymous',
      creditInfo: formData.creditInfo || '',
      creditName: formData.creditName || '',
      resource_2: processedResource2,
      formType: 'view_submission'
    };
    
    // Log payload size for debugging
    const payloadSize = JSON.stringify(payload).length;
    // Payload size calculated
    
    // If payload is too large, consider alternative methods
    if (payloadSize > 10 * 1024 * 1024) {
      console.warn('Payload is very large, may exceed API limits');
      // Consider removing the image or compressing it further
      if (payload.resource_2 && payload.resource_2.length > 1000000) {
        // Removing large image from payload
        payload.resource_2 = ''; // Remove image if it's making the payload too large
      }
    }
    
    // Attempt direct fetch request
    try {
      // Sending request with fetch
      
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload)
      };
      
      // Request options prepared, sending request
      
      const response = await fetch(DIRECTFLOW_SUBMIT_API_URL, requestOptions);
      
      // Response received
      
      // Get response text for logging
      const responseText = await response.text();
      // Response text received
      
      let responseData;
      try {
        responseData = responseText ? JSON.parse(responseText) : {};
      } catch (e) {
        responseData = { raw: responseText };
      }
      
      // Response parsed
      
      if (!response.ok) {
        console.error('API error:', response.status, response.statusText);
        console.error('Response data:', responseData);
        
        // Try fallback method if direct fetch fails
        // Direct fetch failed, trying fallback method
        return await fallbackSubmit(payload);
      }
      
      // Submission successful
      return true;
    } catch (fetchError) {
      console.error('Fetch error:', fetchError);
      
      // Try fallback method if fetch fails
      // Fetch failed, trying fallback method
      return await fallbackSubmit(payload);
    }
  } catch (error) {
    console.error('Error in submitView:', error);
    return false;
  }
};

// Alternative submission method as fallback
async function fallbackSubmit(payload: any): Promise<boolean> {
  try {
    // Trying fallback submission method
    
    // Try using XMLHttpRequest as a fallback
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('POST', DIRECTFLOW_SUBMIT_API_URL, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
                  // XHR request completed
          
          if (xhr.status >= 200 && xhr.status < 300) {
            // Fallback submission successful
            resolve(true);
          } else {
            console.error('Fallback submission failed:', xhr.status, xhr.statusText);
            resolve(false);
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('XHR error occurred');
        resolve(false);
      };
      
      xhr.send(JSON.stringify(payload));
    });
  } catch (error) {
    console.error('Error in fallback submit:', error);
    return false;
  }
} 